import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { ValueDisplayComponent } from './value-display.component';

/**
 * Read display for a `colorpicker` answer (#21239): a small swatch next to the stored hex value,
 * instead of the bare hex text on its own ("#ff0000" reads as a code, not as a color).
 *
 * The swatch border uses `--dd-field-border` (`styles/components/_forms.scss`), which already
 * carries both a light and a dark value — never a hardcoded border color, or the swatch would
 * lose contrast against its own background in the other scheme.
 *
 * Presentational: both inputs come from the row.
 */
@Component({
  selector: 'dd-color-display',
  imports: [ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    /* display: contents — the display must not become a box inside the value span, or the row's
       truncate/multiline behaviour would stop at this element. The swatch + text row layout
       lives on the inner wrapper instead. */
    :host {
      display: contents;
    }

    .dd-color-row {
      display: inline-flex;
      align-items: center;
      gap: 0.5em;
    }

    .dd-color-swatch {
      display: inline-block;
      width: 16px;
      height: 16px;
      flex: none;
      border-radius: 4px;
      border: 1px solid var(--dd-field-border);
    }

    /* No text-transform: the picker stores and shows the hex lowercase (colorpicker.component.ts
       normalizeHex/onMenuOpened) — read and edit must read the same value. */
    .dd-color-value {
      font-family: monospace;
    }
  `,
  template: `
    @if (color(); as color) {
      <span class="dd-color-row">
        <span class="dd-color-swatch" [style.background-color]="color"></span>
        <span class="dd-color-value">{{ color }}</span>
      </span>
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class ColorDisplayComponent extends ReadonlyDisplayBase {
  /** The stored hex string, or `null` for an empty answer — which falls through to the em-dash. */
  readonly color = computed(() => {
    const raw = this.value();
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  });
}
